import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronLeft, Info } from 'lucide-react';

import { cn } from '@/lib/utils';
import { RiseIn } from '@/components/ui/motion';
import { rangsDePriorite, valeurCompacte } from '@/components/ui/stats-compactes';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Illustration } from '@/components/ui/illustration';
import { illustrationPourEntete } from '@/components/ui/header-illustrations';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/** En-tête Repères partagé par les listes, fiches et accueils métier. */

/** Boutons d'action réduits dans la bande (même gabarit partout). */
export const ACTIONS_COMPACTES =
  '[&_button:not([aria-pressed])]:h-8 [&_button:not([aria-pressed])]:px-3 [&_button:not([aria-pressed])]:text-xs';

/** Petite icône d'information: la description en infobulle. */
export const DescriptionInfo = ({ description, className }) => {
  if (!description) return null;
  const texte = typeof description === 'string' ? description : undefined;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={texte || 'À propos de cet écran'}
            data-testid="page-header-info"
            className={cn(
              'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'rounded-md border border-border/60 bg-muted/30 hover:bg-muted',
              className,
            )}
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-pretty">{description}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Les boutons de la bande. Sous 1440 px (voir `.entete-actions` dans
 * index.css), ceux qui commencent par une icône se réduisent à l'icône seule
 * pour tenir sur la rangée; leur libellé complet reste en infobulle
 * (`title`) et dans le nom accessible.
 */
/**
 * Le texte d'un bouton pour son infobulle: un libellé sur deux lignes
 * (« Recalculer » puis « Met à jour toutes les alertes ») donnait
 * « RecalculerMet à jour… » avec `textContent`. Chaque morceau de texte
 * est séparé du suivant.
 */
export const texteBouton = (el) => {
  const morceaux = [];
  const parcours = el.ownerDocument.createTreeWalker(el, 4 /* NodeFilter.SHOW_TEXT */);
  while (parcours.nextNode()) {
    const texte = parcours.currentNode.nodeValue.trim();
    if (texte) morceaux.push(texte);
  }
  return morceaux.join(' — ');
};

const compterActions = (children) =>
  React.Children.toArray(children).reduce(
    (total, enfant) =>
      total +
      (enfant.type === React.Fragment ? compterActions(enfant.props.children) : 1),
    0,
  );

export const ActionsEntete = ({
  children,
  empilees = false,
  grille = false,
  barreOutils = false,
}) => {
  const ref = React.useRef(null);
  const groupe = barreOutils && !grille && compterActions(children) > 1;
  React.useEffect(() => {
    const zone = ref.current;
    if (!zone) return;
    zone.querySelectorAll('button, a').forEach((el) => {
      const precedent = el.dataset.enteteLibelle;
      const aria = el.getAttribute('aria-label');
      const libelle = ((aria && aria !== precedent ? aria : null) || texteBouton(el))
        .replace(/\s+/g, ' ')
        .trim();
      if (!libelle) return;
      if (!el.title || el.title === precedent) el.title = libelle;
      if (!aria || aria === precedent) el.setAttribute('aria-label', libelle);
      el.dataset.enteteLibelle = libelle;
    });
  });
  return (
    <div
      ref={ref}
      data-testid="page-header-actions"
      data-grille={grille ? '' : undefined}
      data-toolbar={groupe ? '' : undefined}
      role="group"
      aria-label="Actions de l’écran"
      className={cn(
        'entete-actions shrink-0',
        grille
          ? // Deux colonnes: chaque bouton remplit sa cellule, libellé sur une
            // ligne; un bouton seul sur la dernière rangée prend toute la largeur.
            'grid w-full min-w-0 grid-cols-2 gap-1.5 [&>*]:min-w-0 [&>*]:w-full [&>*]:whitespace-nowrap [&>:last-child:nth-child(odd)]:col-span-2'
          : 'flex items-center gap-2',
        // Deux boutons l'un sous l'autre, à la hauteur des chiffres en deux
        // rangées (dès 1440 px); trois boutons ou plus: grille 2 colonnes, le
        // dernier impair sur toute la largeur.
        !grille &&
          empilees &&
          'min-[1440px]:flex-col min-[1440px]:items-stretch min-[1440px]:gap-1.5 min-[1440px]:[&:has(>:nth-child(3))]:grid min-[1440px]:[&:has(>:nth-child(3))]:grid-cols-2 min-[1440px]:[&:has(>:nth-child(3))>:last-child:nth-child(odd)]:col-span-2',
        ACTIONS_COMPACTES,
        groupe &&
          'rounded-lg border border-border/60 bg-muted/30 p-0.5 shadow-sm [&_button:not([aria-pressed])]:h-7 [&_button:not([aria-pressed])]:px-2 [&_a]:h-7 [&_a]:px-2',
      )}
    >
      {children}
    </div>
  );
};

/** Conserver les éléments et leurs gestionnaires, y compris les fragments. */
export const actionsAPlat = (children, prefixe = '') => React.Children.toArray(children).flatMap((child) => {
  if (child.type === React.Fragment) return actionsAPlat(child.props.children, `${prefixe}${child.key}/`);
  // Un fragment imbriqué redonne les clés « .0 », « .1 » à ses enfants: sans préfixe, deux boutons
  // de l'en-tête portent la même clé.
  return [prefixe && React.isValidElement(child) ? React.cloneElement(child, { key: `${prefixe}${child.key}` }) : child];
});

/** La largeur du conteneur tient compte du menu latéral, contrairement au viewport. */
function useLargeurEntete(ref) {
  const [largeur, setLargeur] = React.useState(() => typeof window === 'undefined' ? 1200 : window.innerWidth);
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const mesurer = () => {
      const valeur = element.getBoundingClientRect().width;
      if (valeur > 0) setLargeur(Math.round(valeur));
    };
    mesurer();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(mesurer);
    observer?.observe(element);
    window.addEventListener('resize', mesurer);
    return () => { observer?.disconnect(); window.removeEventListener('resize', mesurer); };
  }, [ref]);
  return largeur;
}

/** Une seule partition : aucun élément visible n'est recopié dans Plus. */
export function repartirCommandes({ actions, stats, largeur }) {
  const liste = actionsAPlat(actions);
  const chiffres = Array.isArray(stats) ? stats : [];
  const petits = largeur < 600;
  const nombreActions = petits ? 1 : largeur < 1100 ? 2 : 3;
  // Les boutons primaires sont gardés avant les exports et actions secondaires.
  const ordre = liste.map((action, index) => ({ action, index })).sort((a, b) => {
    const primaire = (el) => React.isValidElement(el) &&
      (el.props['data-header-primary'] ||
        (el.type?.displayName === 'Button' && (!el.props.variant || el.props.variant === 'default')) ||
        /\bbg-primary\b/.test(el.props.className || ''));
    return Number(Boolean(primaire(b.action))) - Number(Boolean(primaire(a.action))) || a.index - b.index;
  });
  const indices = new Set(ordre.slice(0, nombreActions).map(({ index }) => index));
  // Réserver d'abord le titre, les actions, l'illustration et leurs espacements.
  const place = largeur - 320 - Math.min(liste.length, nombreActions) * 112;
  const nombreStats = petits ? 0 : Math.min(largeur < 1000 ? 2 : 6, Math.max(0, Math.floor(place / 92)));
  const rangs = rangsDePriorite(chiffres);
  return {
    actionsVisibles: liste.filter((_, i) => indices.has(i)),
    actionsMasquees: liste.filter((_, i) => !indices.has(i)),
    statsVisibles: chiffres.filter((_, i) => rangs[i] < nombreStats),
    statsMasquees: chiffres.filter((_, i) => rangs[i] >= nombreStats),
  };
}

const TONS_REPERES = { critique: 'text-destructive', attention: 'text-flame-ink', positif: 'text-success' };
const afficher = (valeur) => valeur === null || valeur === undefined || valeur === '' ? '—'
  : typeof valeur === 'number' ? valeur.toLocaleString('fr-FR') : valeur;
const texteReact = (node) => React.Children.toArray(node).map((child) =>
  React.isValidElement(child) ? texteReact(child.props.children) : String(child),
).join(' ').replace(/\s+/g, ' ').trim();

function Repere({ stat, detail = false }) {
  const Composant = stat.onClick ? 'button' : 'div';
  return (
    <Composant
      type={stat.onClick ? 'button' : undefined}
      onClick={stat.onClick}
      data-testid={stat.testid}
      title={[stat.label, stat.aide, `${afficher(stat.valeur)}${stat.unite ? ` ${stat.unite}` : ''}`].filter(Boolean).join(' — ')}
      className={cn(detail ? 'repere-detail' : 'repere-chiffre', stat.onClick && 'repere-interactif', stat.className)}
    >
      <span className={cn('repere-valeur', TONS_REPERES[stat.ton])}>
        {detail ? afficher(stat.valeur) : valeurCompacte(stat) || afficher(stat.valeur)}
        {stat.unite && <small> {stat.unite}</small>}
      </span>
      <span className="repere-libelle">{stat.label}</span>
    </Composant>
  );
}

function DetailAlertes({ alertes }) {
  return <ul className="space-y-3" aria-label="Détail des alertes">{alertes.map((alerte, index) => (
    <li key={index} className="space-y-1 text-sm">
      <p>{alerte.texte}</p>
      {alerte.action?.to ? <Link className="text-primary underline underline-offset-4" to={alerte.action.to}>{alerte.action.label}</Link>
        : alerte.action?.onClick ? <button type="button" className="text-primary underline underline-offset-4" onClick={alerte.action.onClick}>{alerte.action.label}</button> : null}
    </li>
  ))}</ul>;
}

export const CoinDroit = ({ actions, alertes, stats, statsPlus, largeur = 1200, meta }) => {
  const { actionsVisibles, actionsMasquees, statsVisibles, statsMasquees } = repartirCommandes({ actions, stats, largeur });
  const actionExiste = (action, elements = actions) => actionsAPlat(elements).some((el) =>
    React.isValidElement(el) && (
      (action.onClick && el.props.onClick === action.onClick) ||
      (action.to && (el.props.to === action.to || el.props.href === action.to)) ||
      (el.props.children && actionExiste(action, el.props.children))
    ),
  );
  const listeAlertes = (Array.isArray(alertes) ? alertes.filter(Boolean) : []).map((alerte) =>
    alerte.action && actionExiste(alerte.action) ? { ...alerte, action: undefined } : alerte,
  );
  const statut = texteReact(meta).toLocaleLowerCase('fr-FR');
  const sansDoublon = listeAlertes.filter((a) => !a.court || !statut.includes(String(a.court).toLocaleLowerCase('fr-FR')));
  // Le détail d'une alerte portant déjà le statut reste consultable dans Plus.
  const alertesDansPlus = largeur < 600 || sansDoublon.length === 0;
  const aPlus = actionsMasquees.length > 0 || statsMasquees.length > 0 || Boolean(statsPlus) || (alertesDansPlus && listeAlertes.length > 0);
  const [ouvert, setOuvert] = React.useState(false);
  React.useEffect(() => { setOuvert(false); }, [largeur]);
  return (
    <div className="repere-commandes" data-testid="page-header-middle-zone">
      {statsVisibles.length > 0 && <div className="repere-chiffres" role="group" aria-label="Chiffres essentiels">
        {statsVisibles.map((stat, i) => <Repere key={`${stat.label}-${i}`} stat={stat} />)}
      </div>}
      {!alertesDansPlus && listeAlertes.length > 0 && (
        <div role="group" aria-label="Alertes">
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className={cn('repere-alerte', listeAlertes.some(a => a.niveau === 'critique') && 'repere-alerte-critique')} aria-label="Consulter les alertes">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <span>{sansDoublon.length === 1 ? `${sansDoublon[0].nombre ?? ''} ${sansDoublon[0].court || 'À surveiller'}`.trim() : `${sansDoublon.length} alertes`}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end"><DetailAlertes alertes={listeAlertes} /></PopoverContent>
          </Popover>
        </div>
      )}
      {actionsVisibles.length > 0 && <ActionsEntete>{actionsVisibles}</ActionsEntete>}
      {aPlus && <Popover open={ouvert} onOpenChange={setOuvert}>
        <PopoverTrigger asChild>
          <button type="button" className="repere-plus" data-testid="stats-plus" aria-label="Plus : actions et indicateurs complémentaires">
            <span>Plus</span><ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="repere-menu w-80 max-h-[min(70vh,36rem)] max-w-[calc(100vw-2rem)] space-y-3 overflow-y-auto" aria-label="Éléments complémentaires">
          {actionsMasquees.length > 0 && <div className="repere-actions-secondaires" role="group" aria-label="Autres actions">{actionsMasquees}</div>}
          {statsMasquees.length > 0 && <div data-testid="stats-reportees" className="space-y-1" role="group" aria-label="Autres indicateurs">
            {statsMasquees.map((stat, i) => <Repere key={`${stat.label}-${i}`} stat={stat} detail />)}
          </div>}
          {alertesDansPlus && listeAlertes.length > 0 && <DetailAlertes alertes={listeAlertes} />}
          {statsPlus}
        </PopoverContent>
      </Popover>}
    </div>
  );
};

const PageHeader = ({
  eyebrow, title, description, actions, backTo, backLabel = 'Retour', meta, stats,
  statsPlus, statsDisposition, alertes, alertesSousTitre, actionsEmpilees, actionsDisposition,
  illustration, illustrationVariant = 'main', illustrationTestId = 'page-header-illustration',
  className, ref, children, ...props
}) => {
  const { pathname } = useLocation();
  const illustrationEntete = illustration || illustrationPourEntete(pathname);
  const bande = React.useRef(null);
  const largeur = useLargeurEntete(bande);
  return (
    <RiseIn as="header" ref={ref} data-testid="page-header" className={cn('entete-reperes', className)} {...props}>
      <div ref={bande} className="repere-bande">
        <div data-testid="page-header-title-zone" className="repere-identite">
          {(backTo || eyebrow) && <p className="repere-surtitre">
            {backTo && <Link to={backTo} className="repere-retour"><ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />{backLabel}</Link>}
            {eyebrow && <span title={typeof eyebrow === 'string' ? eyebrow : undefined}>{eyebrow}</span>}
          </p>}
          <div className="repere-titre-ligne">
            <div className="repere-titre-principal">
              <h1 title={typeof title === 'string' ? title : undefined}>{title}</h1>
              <DescriptionInfo description={description} />
            </div>
            {meta && <div className="repere-meta">{meta}</div>}
          </div>
        </div>
        <CoinDroit actions={actions} stats={stats} statsPlus={statsPlus} alertes={alertes} largeur={largeur} meta={meta} />
        {illustrationEntete && <div data-testid={illustrationTestId} className="repere-illustration" aria-hidden="true">
          <Illustration name={illustrationEntete} variant={illustrationVariant} largeurMax={120} className="h-full w-full object-contain dark:brightness-90" />
        </div>}
      </div>
      {children}
    </RiseIn>
  );
};
PageHeader.displayName = 'PageHeader';
export { PageHeader };
